import { ExceptionDayInfo, OpeningHoursInfo, ProductOpeningHoursResponse } from '@leid/api-locations'
import { ProductAvailabilityInfo, ProductLocationAvailability, ProductLocationAvailabilityInfo } from '@leid/api-orders'
import moment from 'moment'

import { checkTimeRange, getDayOfWeek } from 'components/partials/shared/RentProduct/RentProductLogic/helpers/time'
import { parseToMoment, utcNow } from 'utils/dates'

export const getLocationAvailabilityInfo = (
  locationId: number,
  availabilityInfo: ProductAvailabilityInfo,
): ProductLocationAvailability | undefined => {
  const availabilities = availabilityInfo.locations.find((s) => s.locationId === locationId)
  return availabilities
}

export const isProductAvailable = (
  locationId: number,
  rentedFrom: Date,
  rentedTo: Date,
  isStart: boolean | undefined,
  availabilityInfo: ProductAvailabilityInfo,
  openingHours: ProductOpeningHoursResponse[],
  ignoreRerent: boolean,
  checkTime: boolean,
  amount: number,
): boolean => {
  const availabilities = getLocationAvailabilityInfo(locationId, availabilityInfo)
  if (!availabilities) {
    return false
  }

  const availabilitiesChange = getAvailabilitiesChanges(rentedFrom, rentedTo, availabilities, ignoreRerent)
  if (availabilitiesChange.some((s) => s < amount)) {
    return false
  }

  // Check opening hours for start and end of period
  const startsOrEndsInWorkingHours = checkIfPeriodStartsEndsInWorkingHours(
    rentedFrom,
    rentedTo,
    locationId,
    isStart,
    openingHours,
    checkTime,
  )

  if (!startsOrEndsInWorkingHours) {
    return false
  }

  return true
}

export const getAvailabilitiesChanges = (
  start: Date,
  end: Date,
  availabilities: ProductLocationAvailability,
  ignoreRerent: boolean,
): number[] => {
  const calculationStartTime = utcNow()
  const startDate = start > calculationStartTime ? start : calculationStartTime

  if (startDate <= calculationStartTime && end <= calculationStartTime) {
    return []
  }

  const startAvailability = getClosestAvailabilityInfo(startDate, availabilities.availabilityInfo)

  const availabilitiesDuringDay = availabilities.availabilityInfo.filter((s) => s.date >= startDate && s.date < end)

  // Add start availability
  availabilitiesDuringDay.unshift(startAvailability)

  const duringPeriodAmountChange = availabilitiesDuringDay.map((s) => {
    if (ignoreRerent && s.isRerent) {
      return 0
    }

    return s.amount
  })

  return duringPeriodAmountChange
}

export const getClosestAvailabilityInfo = (
  date: Date,
  availabilityInfo: ProductLocationAvailabilityInfo[],
): ProductLocationAvailabilityInfo => {
  let result: ProductLocationAvailabilityInfo = {
    amount: 0,
    date: utcNow(),
  }
  for (const info of availabilityInfo) {
    if (info.date <= date) {
      result = info
    } else {
      break
    }
  }

  return result
}

export const checkIfPeriodStartsEndsInWorkingHours = (
  rentedFrom: Date,
  rentedTo: Date,
  locationId: number,
  isStart: boolean | undefined,
  openingHours: ProductOpeningHoursResponse[],
  checkTime: boolean,
): boolean => {
  if (isStart === undefined || isStart === true) {
    const startIsInWorkingHours = checkIfDateIsInWorkingHours(rentedFrom, locationId, openingHours, false, checkTime)
    if (!startIsInWorkingHours) {
      return false
    }
  }

  if (isStart === undefined || isStart === false) {
    const endIsInWorkingHours = checkIfDateIsInWorkingHours(rentedTo, locationId, openingHours, true, checkTime)
    if (!endIsInWorkingHours) {
      return false
    }
  }

  return true
}

export const getProductAvailabilitiesInfoForPeriod = (
  locationId: number,
  start: Date,
  end: Date | undefined,
  availabilityInfo: ProductAvailabilityInfo,
): ProductLocationAvailabilityInfo[] => {
  const availabilities = getLocationAvailabilityInfo(locationId, availabilityInfo)
  if (!availabilities) {
    return []
  }

  const startAvailability = getClosestAvailabilityInfo(start, availabilities.availabilityInfo)
  const availabilitiesDuringPeriod = availabilities.availabilityInfo.filter(
    (s) => s.date >= start && (!end || s.date <= end),
  )

  const availabilitiesChange: ProductLocationAvailabilityInfo[] = []
  availabilitiesChange.push(startAvailability)
  availabilitiesChange.push(...availabilitiesDuringPeriod)

  return availabilitiesChange
}

const checkIfDateIsInWorkingHours = (
  date: Date,
  locationId: number,
  openingHoursInfo: ProductOpeningHoursResponse[],
  isReturn: boolean,
  checkTime: boolean,
) => {
  const localDate = parseToMoment(date)
  if (!localDate) {
    return false
  }

  const openingHours = selectOpeningHoursForDay(date, locationId, openingHoursInfo)

  if (isReturn && openingHours.returnOutsideOpeningHours) {
    return true
  }

  if (openingHours.openingHours) {
    if (openingHours.openingHours.closed) {
      // Location is closed
      return false
    }

    if (checkTime) {
      const isInTimeRange = checkTimeRange(localDate.hours(), openingHours.openingHours.openingHours, isReturn)
      if (!isInTimeRange) {
        // Hour is not in opening hours
        return false
      }
    }
  }

  for (const exceptionDay of openingHours.exceptionDays) {
    if (exceptionDay.closed) {
      return true
    }

    if (checkTime) {
      const isInTimeRange = checkTimeRange(localDate.hours(), exceptionDay.openingHours, isReturn)
      if (!isInTimeRange) {
        // Hour is not in opening hours
        return true
      }
    }
  }

  return true
}

const selectOpeningHoursForDay = (
  date: Date,
  locationId: number,
  openingHoursData: ProductOpeningHoursResponse[],
): {
  returnOutsideOpeningHours: boolean
  openingHours: OpeningHoursInfo | undefined
  exceptionDays: ExceptionDayInfo[]
} => {
  const info = openingHoursData.find((s) => s.locationId === locationId)
  if (!info) {
    return {
      returnOutsideOpeningHours: false,
      openingHours: undefined,
      exceptionDays: [],
    }
  }

  const dayOfWeek = getDayOfWeek(date)

  const openingHoursDayInfo = info.openingHours.openingHours.find((s) => s.dayOfWeek === dayOfWeek)
  const exceptionDays = info.openingHours.exceptionDays.filter((s) => moment(s.date).isSame(date, 'date'))

  return {
    returnOutsideOpeningHours: info.openingHours.returnOutsideOpeningHours,
    openingHours: openingHoursDayInfo,
    exceptionDays,
  }
}

export const getClosestAvailabilityAmount = (
  date: Date,
  availabilityInfo: ProductLocationAvailabilityInfo[],
): number => {
  let result = 0
  for (const info of availabilityInfo) {
    if (info.date <= date) {
      result = info.amount
    } else {
      break
    }
  }

  return result
}

export const getClosestUnavailableDate = (
  date: Date,
  availabilityInfo: ProductLocationAvailability | undefined,
  isRerentOrder: boolean,
  amount: number,
): Date | undefined => {
  if (!availabilityInfo) {
    return undefined
  }

  for (const info of availabilityInfo.availabilityInfo) {
    if (info.date >= date) {
      const availableAmount = getAvailableAmount(info, isRerentOrder)
      if (availableAmount < amount) {
        return info.date
      }
    }
  }
  return undefined
}

const getAvailableAmount = (info: ProductLocationAvailabilityInfo, isRerentOrder: boolean): number => {
  if (info.isRerent) {
    if (isRerentOrder) {
      return info.amount
    }
    return 0
  }

  return info.amount
}
